import { useState, type FC } from "react";
import type { EmailConnectionType } from "../../../models/EmailConnectionModel";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { theme } from "../../../theme";
import Modal from "../Modal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import Body from "../../atoms/typography/Body";
import IconButton from "../../atoms/buttons/IconButton";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import Alert from "../Alert";
import { useDeleteClientEmailConnection } from "../../../graphql/hooks/documentHandler/useDeleteClientEmailConnection";
import { useTranslation } from "react-i18next";

interface Props {
  emailConnections: Array<EmailConnectionType>;
}

const EmailConnectionContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UpdateEmailConnectionsButton: FC<Props> = ({ emailConnections }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCompletedDeleteEmailConnection = (result: any) => {
    if (result?.aml?.deleteClientEmailConnection?.success === true) {
      setSuccessMessage(t("documentHandler.theEmailConnectionHasBeenRemoved"));
      setErrorMessage("");
    } else {
      setErrorMessage(t("documentHandler.emailConnectionCouldNotBeRemoved"));
    }
  };
  const onErrorDeleteEmailConnection = () => {
    setErrorMessage(t("documentHandler.emailConnectionCouldNotBeRemoved"));
  };
  const {
    deleteClientEmailConnection,
    error: errorDeleteEmail,
    loading: loadingDeleteEmal,
  } = useDeleteClientEmailConnection({
    onCompleted: onCompletedDeleteEmailConnection,
    onError: onErrorDeleteEmailConnection,
  });

  const onDeleteEmailClientConnection = async (emailId: string) => {
    try {
      await deleteClientEmailConnection({
        variables: { id: emailId },
      });
    } catch (e) {
      setErrorMessage(t("documentHandler.emailConnectionCouldNotBeRemoved"));
    }
  };

  return (
    <div>
      <OutlinedButton
        color={theme.colors.primary}
        title={`${t("documentHandler.handleEmailConnections")} (${
          emailConnections?.length
        }st)`}
        onClick={(e?: React.MouseEvent) => {
          e?.stopPropagation();
          setModalOpen(true);
        }}
      />

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={400}
          title={t("documentHandler.handleEmailConnections")}
        >
          {emailConnections.map((email) => (
            <EmailConnectionContainer key={email.id}>
              <div>
                <BodySmall color={theme.colors.textSecondary}>
                  {t("documentHandler.latestUpdate")}:{" "}
                  {dayjs(email.modifiedAt).format("YYYY-MM-DD HH:mm")}
                </BodySmall>
                <Body>{email.emailAddress}</Body>
              </div>
              <IconButton
                icon={<TrashIcon color={theme.colors.error} />}
                color={theme.colors.error20}
                loading={loadingDeleteEmal}
                onClick={() => onDeleteEmailClientConnection(email.id)}
              />
            </EmailConnectionContainer>
          ))}

          <HorizontalSpacer spacing={1} />
          {errorMessage && (
            <Alert
              type="error"
              title={errorMessage}
              message={errorDeleteEmail?.toString()}
            />
          )}
          {successMessage && <Alert type="success" title={successMessage} />}
          <HorizontalSpacer spacing={1} />
        </Modal>
      )}
    </div>
  );
};

export default UpdateEmailConnectionsButton;
